import { memo, ReactNode, useState } from "react";

import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import {
  XCircleFill,
  ArrowLeft,
} from "@hl/base-components/lib/assets/icons.generated";
import Table from "@hl/base-components/lib/table/Table";
import { ContextProvider as TableContextProvider } from "@hl/base-components/lib/table/context";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { ServerError } from "@hl/shared-features/lib/features/layout";
import { AppSEO } from "@hl/shared-features/lib/features/layout/AppSEO";
import {
  Text,
  Container,
  Title,
  Button,
  AppShell,
  Space,
  Group,
} from "@mantine/core";
import { Link } from "react-router-dom";

import { TOOLS_URL } from "config";
import ProvidedEmailRow from "~/features/account/email/ProvidedEmailRow";
import { UnlinkAllEmailsModal } from "~/features/account/email/UnlinkAllEmailsModal";
import { useGetAccountSettingsQuery } from "~/features/account/settings.graphql.generated";
import { AppFooter } from "~/features/layout/Footer";
import { AppHeader } from "~/features/layout/Header";

const EmailSettingsShell = ({ children }: { children: ReactNode }) => (
  <>
    <AppSEO title="Email settings" />
    <AppShell
      padding="md"
      header={
        <AppHeader
          leftButtons={
            <Link to={TOOLS_URL.pages.list}>
              <Button
                color={SECONDARY_COLOR}
                leftIcon={<ArrowLeft height={12} />}
              >
                Back
              </Button>
            </Link>
          }
        />
      }
      footer={<AppFooter />}
    >
      {children}
    </AppShell>
  </>
);

const EmailSettings = () => {
  const { data, loading, error } = useGetAccountSettingsQuery();
  const [isRemoveAllModalOpen, setIsRemoveAllModalOpen] =
    useState<boolean>(false);

  const allowedCreators =
    data?.getAccountSettings.marketingPreferences.allowedCreators.filter(
      (allowedCreator) => allowedCreator.email
    );
  const userEmail = data?.getAccountSettings.socials.email?.address;

  if (loading) {
    return (
      <EmailSettingsShell>
        <LoadingPage />
      </EmailSettingsShell>
    );
  }

  if (error) {
    return <ServerError />;
  }

  return (
    <EmailSettingsShell>
      <Container size="sm">
        <Title order={4}>Email settings</Title>
        <Text mt="sm" size="sm">
          You can manage the access of the creators you have shared your email
          with by unlinking your email from a specific creator or from all
          creators.
        </Text>
        <Space h="lg" />
        {allowedCreators?.length ? (
          <TableContextProvider>
            <Table
              tableName="EmailProviderToCreatorTable"
              striped
              data={allowedCreators}
              padding="0"
              sortOptions={[]}
              loading={false}
              uniqueEmptyMessage={false}
              tableTitle={
                <Group position="apart">
                  <Text size="sm" weight={500}>
                    {`You have provided the email ${userEmail} to ${
                      allowedCreators?.length ?? 0
                    } creators.`}
                  </Text>

                  <Button
                    leftIcon={<XCircleFill />}
                    color={SECONDARY_COLOR}
                    onClick={() => setIsRemoveAllModalOpen(true)}
                  >
                    Unlink all emails
                  </Button>
                </Group>
              }
              titleSpacing="lg"
              loadingComponent={null}
              emptyComponent={null}
              tableHeaderRow={
                <tr>
                  <th>
                    <Text size="sm" weight={500}>
                      Creator wallet/ENS
                    </Text>
                  </th>
                  <th>
                    <Text size="sm" weight={500}>
                      Provided email on date and time
                    </Text>
                  </th>
                  <th>
                    <Text size="sm" weight={500}>
                      Unlink email
                    </Text>
                  </th>
                </tr>
              }
              /* eslint-disable  @typescript-eslint/no-explicit-any */
              tableRow={(key: string, data: any) => {
                return <ProvidedEmailRow key={key} {...data} />;
              }}
              itemsPerPage={4}
            />
          </TableContextProvider>
        ) : (
          <Text size="sm" weight={500}>
            You have provided your email to 0 creators.
          </Text>
        )}
      </Container>
      <UnlinkAllEmailsModal
        isOpen={isRemoveAllModalOpen}
        onClose={() => setIsRemoveAllModalOpen(false)}
        allowedCreatorsTotal={allowedCreators?.length || 0}
      />
    </EmailSettingsShell>
  );
};

export default memo(EmailSettings);
